import { type ReactNode, useEffect, useMemo } from "react";
import { type Control, Controller, type FieldPath, useWatch } from "react-hook-form";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { MarkdownEditor } from "../../../../../../components/MarkdownEditor";
import { MultiSelectCombobox } from "../MultiSelectCombobox";
import {
  AGGREGATOR_INFORMATION,
  EQUIPMENT_DISTRIBUTION,
  EQUIPMENT_TYPE_OPTIONS,
  FARMER_TRAINING,
  INPUT_CATEGORY_OPTIONS,
  INPUT_DISTRIBUTION,
} from "../../constants";
import type {
  InfoHubContentTypeStepProps,
  InfoHubFormValues,
  SelectOption,
  TopicOption,
} from "../../types";
import { formatAggregatorLabel } from "../../../../../../utils/adminArea";

// Reads the message of a react-hook-form error for the given field name.
const getErrorMessage = (errors: Record<string, unknown>, name: string): string => {
  const error = errors[name] as { message?: unknown } | undefined;
  return typeof error?.message === "string" ? error.message : "";
};

interface FormFieldProps {
  id: string;
  label: string;
  helperText?: string;
  errorMessage?: string;
  tooltip?: ReactNode;
  children: ReactNode;
}

const FormField = ({ id, label, helperText, errorMessage, tooltip, children }: FormFieldProps) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="font-semibold text-foreground">
        {label}
      </Label>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <WFPButton
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              aria-label={`${label} help`}
            >
              <Info className="size-3.5" />
            </WFPButton>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-80">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
    {helperText ? <p className="text-muted-foreground text-sm">{helperText}</p> : null}
    {children}
    {errorMessage ? (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    ) : null}
  </div>
);

interface SelectFieldProps {
  control: Control<InfoHubFormValues>;
  name: FieldPath<InfoHubFormValues>;
  label: string;
  placeholder: string;
  options: SelectOption[];
  errorMessage?: string;
  helperText?: string;
  tooltip?: ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  half?: boolean;
}

// A single-select field bound to a react-hook-form Controller. The selected
// label is rendered by radix from the matched option, so the stored value only
// needs a correct `.value`.
const SelectField = ({
  control,
  name,
  label,
  placeholder,
  options,
  errorMessage,
  helperText,
  tooltip,
  disabled = false,
  isLoading = false,
  half = false,
}: SelectFieldProps) => {
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const selected = field.value as SelectOption | null | undefined;
        return (
          <FormField
            id={name}
            label={label}
            helperText={helperText}
            errorMessage={errorMessage}
            tooltip={tooltip}
          >
            <div className={half ? "md:w-1/2" : undefined}>
              <Select
                value={selected ? String(selected.value) : undefined}
                onValueChange={nextValue =>
                  field.onChange(options.find(option => String(option.value) === nextValue) ?? null)
                }
                disabled={disabled || isLoading}
              >
                <SelectTrigger
                  id={name}
                  aria-invalid={!!errorMessage}
                  onBlur={field.onBlur}
                  className="h-10 w-full border-border"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Spinner className="size-4 shrink-0" />
                      {t("multiselect.default.loading")}
                    </span>
                  ) : (
                    <SelectValue placeholder={placeholder} />
                  )}
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-72">
                  {options.map(option => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FormField>
        );
      }}
    />
  );
};

export const InfoHubContentTypeStep = ({
  methods,
  contentTypes,
  topics,
  aggregators,
  units,
  loading,
  isCopy = false,
}: InfoHubContentTypeStepProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    resetField,
    getValues,
    setValue,
  } = methods;

  const selectedContentType = useWatch({ control, name: "content_type" });
  const contentTypeValue = selectedContentType ? String(selectedContentType.value) : undefined;
  const contentTypeLabel = selectedContentType?.label
    ? String(selectedContentType.label)
    : t("infoHub.form.contentHelperTextFallbackType");

  const isMCFContentType = useMemo(
    () => (contentTypeValue ? contentTypeValue.includes("MCF") : false),
    [contentTypeValue]
  );
  const isInputDistribution = contentTypeValue === INPUT_DISTRIBUTION;
  const isEquipmentDistribution = contentTypeValue === EQUIPMENT_DISTRIBUTION;
  // Distribution subjects can be assigned to only one aggregator organization.
  const isDistribution = isInputDistribution || isEquipmentDistribution;
  const canSelectAllAggregators =
    contentTypeValue === AGGREGATOR_INFORMATION || contentTypeValue === FARMER_TRAINING;

  // When the content type changes audience, drop topics/aggregators that no
  // longer match the target. Reproduces the legacy effect 1:1.
  useEffect(() => {
    const selectedTopics = getValues("topics");
    const stillMatches =
      selectedTopics &&
      selectedTopics.some(topic =>
        isMCFContentType ? topic.target === "MCF_FOUNDATION" : topic.target === "AGGREGATOR"
      );
    if (!stillMatches) {
      setValue("topics", []);
      resetField("aggregators");
    }
  }, [isMCFContentType, getValues, resetField, setValue]);

  // Clear the distribution group that does not match the selected type so stale
  // values are not submitted and their validation errors clear.
  useEffect(() => {
    if (!isInputDistribution) {
      setValue("input_distribution_category", undefined);
      setValue("input_distribution_details", undefined);
      setValue("input_distribution_quantity", undefined);
      setValue("input_distribution_unit_of_measure", undefined);
    }
    if (!isEquipmentDistribution) {
      setValue("equipment_distribution_type", undefined);
      setValue("equipment_distribution_details", undefined);
      setValue("equipment_distribution_quantity", undefined);
    }
  }, [isInputDistribution, isEquipmentDistribution, setValue]);

  // Switching to a distribution type caps any existing multi-selection at one.
  useEffect(() => {
    if (isDistribution) {
      const selected = getValues("aggregators");
      if (selected && selected.length > 1) {
        setValue("aggregators", selected.slice(0, 1));
      }
    }
  }, [isDistribution, getValues, setValue]);

  const contentTypeOptions = useMemo<SelectOption[]>(
    () => contentTypes.map(contentType => ({ value: contentType, label: contentType })),
    [contentTypes]
  );

  const inputCategoryOptions = useMemo<SelectOption[]>(
    () =>
      INPUT_CATEGORY_OPTIONS.map(option => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );

  const equipmentTypeOptions = useMemo<SelectOption[]>(
    () =>
      EQUIPMENT_TYPE_OPTIONS.map(option => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );

  const topicOptions = useMemo<TopicOption[]>(
    () =>
      topics
        .filter(topic =>
          isMCFContentType ? topic.target === "MCF_FOUNDATION" : topic.target === "AGGREGATOR"
        )
        .map(topic => ({ value: topic.id, label: topic.name, target: topic.target })),
    [topics, isMCFContentType]
  );

  const aggregatorOptions = useMemo(
    () =>
      aggregators
        .filter(aggregator =>
          isMCFContentType
            ? aggregator.managed_individuals.some(
                individual => individual === "mastercard_foundation_participants"
              )
            : aggregator.managed_individuals.some(individual => individual === "farmers")
        )
        .map(aggregator => ({ value: aggregator.id, label: formatAggregatorLabel(aggregator) })),
    [aggregators, isMCFContentType]
  );

  const tooltipContent = (
    <div>
      <span className="font-semibold">{t("infoHub.form.aggregatorInformationHeading")}</span>
      {t("infoHub.form.aggregatorInformationToolTip")}
      <br />
      <span className="font-semibold">{t("infoHub.form.farmerTrainingHeading")}</span>
      {t("infoHub.form.farmerTrainingToolTip")}
    </div>
  );

  return (
    <div className="space-y-6 py-2">
      {/* Content type */}
      <SelectField
        control={control}
        name="content_type"
        label={t("infoHub.form.contentType")}
        placeholder={t("infoHub.form.contentTypePlaceholder")}
        options={contentTypeOptions}
        errorMessage={getErrorMessage(errors, "content_type")}
        tooltip={tooltipContent}
        disabled={isCopy}
        half
      />

      {/* Input Distribution details */}
      {isInputDistribution && (
        <>
          <SelectField
            control={control}
            name="input_distribution_category"
            label={t("infoHub.form.inputCategory")}
            placeholder={t("infoHub.form.inputCategoryPlaceholder")}
            options={inputCategoryOptions}
            errorMessage={getErrorMessage(errors, "input_distribution_category")}
            half
          />
          <Controller
            control={control}
            name="input_distribution_details"
            render={({ field }) => (
              <FormField
                id="input_distribution_details"
                label={t("infoHub.form.inputDescription")}
                helperText={t("infoHub.form.inputDescriptionHelperText")}
                errorMessage={getErrorMessage(errors, "input_distribution_details")}
              >
                <Input
                  id="input_distribution_details"
                  value={(field.value as string) ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-invalid={!!getErrorMessage(errors, "input_distribution_details")}
                  className="border-border"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="input_distribution_quantity"
            render={({ field }) => (
              <FormField
                id="input_distribution_quantity"
                label={t("infoHub.form.quantity")}
                errorMessage={getErrorMessage(errors, "input_distribution_quantity")}
              >
                <Input
                  id="input_distribution_quantity"
                  type="number"
                  min={1}
                  value={(field.value as number | undefined) ?? ""}
                  onChange={event =>
                    field.onChange(
                      event.target.value === "" ? undefined : Number(event.target.value)
                    )
                  }
                  onBlur={field.onBlur}
                  placeholder={t("infoHub.form.quantityPlaceholder")}
                  aria-invalid={!!getErrorMessage(errors, "input_distribution_quantity")}
                  className="border-border md:w-1/2"
                />
              </FormField>
            )}
          />
          <SelectField
            control={control}
            name="input_distribution_unit_of_measure"
            label={t("infoHub.form.unitOfMeasurement")}
            placeholder={t("infoHub.form.unitOfMeasurementPlaceholder")}
            options={units}
            errorMessage={getErrorMessage(errors, "input_distribution_unit_of_measure")}
            isLoading={loading?.units}
            half
          />
        </>
      )}

      {/* Equipment Distribution details */}
      {isEquipmentDistribution && (
        <>
          <SelectField
            control={control}
            name="equipment_distribution_type"
            label={t("infoHub.form.equipmentType")}
            placeholder={t("infoHub.form.equipmentTypePlaceholder")}
            options={equipmentTypeOptions}
            errorMessage={getErrorMessage(errors, "equipment_distribution_type")}
            half
          />
          <Controller
            control={control}
            name="equipment_distribution_quantity"
            render={({ field }) => (
              <FormField
                id="equipment_distribution_quantity"
                label={t("infoHub.form.quantity")}
                errorMessage={getErrorMessage(errors, "equipment_distribution_quantity")}
              >
                <Input
                  id="equipment_distribution_quantity"
                  type="number"
                  min={1}
                  value={(field.value as number | undefined) ?? ""}
                  onChange={event =>
                    field.onChange(
                      event.target.value === "" ? undefined : Number(event.target.value)
                    )
                  }
                  onBlur={field.onBlur}
                  placeholder={t("infoHub.form.quantityPlaceholder")}
                  aria-invalid={!!getErrorMessage(errors, "equipment_distribution_quantity")}
                  className="border-border md:w-1/2"
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="equipment_distribution_details"
            render={({ field }) => (
              <FormField
                id="equipment_distribution_details"
                label={t("infoHub.form.equipmentDescription")}
                helperText={t("infoHub.form.equipmentDescriptionHelperText")}
                errorMessage={getErrorMessage(errors, "equipment_distribution_details")}
              >
                <Textarea
                  id="equipment_distribution_details"
                  value={(field.value as string) ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-invalid={!!getErrorMessage(errors, "equipment_distribution_details")}
                  className="border-border"
                />
              </FormField>
            )}
          />
        </>
      )}

      {/* Title */}
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <FormField
            id="name"
            label={t("infoHub.form.title")}
            helperText={t("infoHub.form.titleHelperText")}
            errorMessage={getErrorMessage(errors, "name")}
          >
            <Input
              id="name"
              value={(field.value as string) ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t("infoHub.form.titlePlaceholder")}
              aria-invalid={!!getErrorMessage(errors, "name")}
              className="border-border"
            />
          </FormField>
        )}
      />

      {/* Topics */}
      <Controller
        control={control}
        name="topics"
        render={({ field }) => (
          <FormField
            id="topics"
            label={t("infoHub.form.topic")}
            helperText={t("infoHub.form.topicHelperText")}
            errorMessage={getErrorMessage(errors, "topics")}
          >
            <MultiSelectCombobox<TopicOption>
              id="topics"
              options={topicOptions}
              value={(field.value as TopicOption[]) ?? []}
              onChange={field.onChange}
              placeholder={t("infoHub.form.topicPlaceholder")}
              errorMessage={getErrorMessage(errors, "topics")}
              isLoading={loading?.topics}
            />
          </FormField>
        )}
      />

      {/* Content */}
      <Controller
        control={control}
        name="content"
        render={({ field }) => (
          <FormField
            id="content"
            label={t("infoHub.form.content")}
            helperText={t("infoHub.form.contentHelperText", { type: contentTypeLabel })}
            errorMessage={getErrorMessage(errors, "content")}
          >
            <MarkdownEditor field={field} />
          </FormField>
        )}
      />

      {/* Aggregators */}
      <Controller
        control={control}
        name="aggregators"
        render={({ field }) => (
          <FormField
            id="aggregators"
            label={t("infoHub.form.aggregators")}
            helperText={
              isDistribution
                ? t("infoHub.form.aggregatorsSingleHelperText")
                : t("infoHub.form.aggregatorsHelperText")
            }
            errorMessage={getErrorMessage(errors, "aggregators")}
          >
            <MultiSelectCombobox
              id="aggregators"
              options={aggregatorOptions}
              value={(field.value as { value: number | string; label: string }[]) ?? []}
              onChange={field.onChange}
              placeholder={t("infoHub.form.aggregatorsPlaceholder")}
              searchPlaceholder={t("infoHub.form.aggregatorsSearchPlaceholder")}
              errorMessage={getErrorMessage(errors, "aggregators")}
              isLoading={loading?.aggregators}
              singleSelect={isDistribution}
              showSelectAll={canSelectAllAggregators}
              summarizeSelectionAfter={3}
            />
          </FormField>
        )}
      />
    </div>
  );
};
